import { memo, useCallback } from "react";
import TableCellComponent from "./TableCellComponent";

interface EditableTextCellProps {
  value: string;
  field: string; // Generic field name instead of keyof specific type
  identifier: string | number; // Generic identifier that can be string or number
  index: number;
  maxWidth?: number | string;
  isEditable: boolean;
  hasError: boolean;
  onBlur: (
    e: React.FocusEvent<HTMLTableCellElement>,
    field: string,
    identifier: string | number,
    value: string,
    index: number,
  ) => void;
  onInput: (e: React.FormEvent<HTMLTableCellElement>, field: string, index: number) => void;
  onFocus: (e: React.FocusEvent<HTMLDivElement>) => void;
}

const EditableTextCell = memo<EditableTextCellProps>(
  ({
    value,
    field,
    identifier,
    index,
    maxWidth,
    isEditable,
    hasError,
    onBlur,
    onInput,
    onFocus,
  }) => {
    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLTableCellElement>) => {
        onBlur(e, field, identifier, e.target.innerText, index);
      },
      [onBlur, field, identifier, index],
    );

    const handleInput = useCallback(
      (e: React.FormEvent<HTMLTableCellElement>) => {
        onInput(e, field, index);
      },
      [onInput, field, index],
    );

    return (
      <TableCellComponent
        sx={{
          backgroundColor: isEditable ? "var(--white)" : "var(--offwhite-400)",
          border: "2px solid var(--silver-150)",
          borderColor: hasError ? "var(--red-800)" : "var(--silver-150)",
          fontWeight: "regular",
          textAlign: "left",
          color: "var(--black)",
          maxWidth,
          // Fix height and line-height issues for stable cursor (similar to EditableNumberCell)
          height: "48px",
          minHeight: "48px",
          lineHeight: "48px", // Match height for consistent vertical centering
          padding: "0 8px", // Only horizontal padding
          // Stable layout properties
          outline: "none",
          boxSizing: "border-box",
          verticalAlign: "baseline", // Consistent baseline alignment
          // Override inherited styles that cause jumping
          py: "0 !important", // Force no vertical padding from parent
          // Contenteditable specific fixes
          "&[contenteditable='true']": {
            outline: "none",
            userSelect: "text",
            cursor: "text",
            lineHeight: "48px", // Keep consistent line height
            // Fix for empty content cursor position
            "&:empty": {
              lineHeight: "48px",
              minHeight: "48px",
            },
          },
          // Focus state with border highlight
          "&:focus": {
            outline: "none",
            lineHeight: "48px",
            borderColor: hasError ? "var(--red-800)" : "initial", // Focus border highlight
            boxShadow: hasError ? "0 0 0 1px var(--red-800)" : "initial",
          },
        }}
        onFocus={onFocus}
        contentEditable={isEditable}
        suppressContentEditableWarning
        onBlur={handleBlur}
        onInput={handleInput}
      >
        {value}
      </TableCellComponent>
    );
  },
);

EditableTextCell.displayName = "EditableTextCell";

export default EditableTextCell;
